import "./queueFleet.css";
import "./importClients.css";
import { api, ApiError } from "../api";
import { pill, tableRegion } from "./shared/blocks";
import { createLifecycle, isAbort } from "./shared/lifecycle";
import { clientHref, h, tidy } from "./shared/safe";
import {
  ACTIONS, ACTION_LABELS, ACTION_LABELS_DONE, DEFAULT_OPTIONS, FIELD_ALIASES, KNOWN_FIELDS, MAX_ROWS, POLICY_FIELDS, TOO_MANY_MESSAGE,
  actionTone, buildBody, canDryRun, describeFailure, isAppliedReply, isValidPreview, parseImportReply, parseInput, planFor, requestKey,
  type ImportOptions, type ImportReply, type ImportRow, type OnError, type OnExisting, type ParsedInput, type ResultRow, type RowAction,
} from "./importModel";

// Bulk client import. Paste JSON or CSV (or choose a file), run a DRY RUN, read what would happen row by row, then confirm.
//   - Nothing leaves the page until "Run dry run" is pressed, and a dry run writes nothing (POST /v1/clients/import, dryRun:true).
//   - "Confirm import" stays unavailable until a dry run of EXACTLY the current rows and options succeeded; any change to the text, the
//     file or the options discards the preview. The sentence next to it comes from the preview's own counts.
//   - A failed or unreadable confirm is "not known": the page says so and asks for a new dry run (importing is idempotent by slug)
//     instead of sending again.
// Every string from the file or the server is untrusted text (h()); nothing is parsed as HTML.

const MAX_FILE_BYTES = 2 * 1024 * 1024;
const FILE_RE = /\.(json|csv|txt)$/i;
const FEW = 10; // up to this many created/updated clients get their own link after the import

interface Preview {
  key: string;
  rows: ImportRow[];
  reply: ImportReply;
}
interface Problem {
  kind: "refused" | "uncertain";
  headline: string;
  detail: string | null;
}

export async function importClientsView(root: HTMLElement): Promise<void> {
  const shell = h("div", { class: "qf qf-import" });
  root.replaceChildren(shell);
  const life = createLifecycle(shell);

  let parsed: ParsedInput = parseInput("");
  const options: ImportOptions = { ...DEFAULT_OPTIONS };
  let fileNote: string | null = null;
  let busy: "dry" | "confirm" | null = null;
  let preview: Preview | null = null;
  let problem: Problem | null = null;
  let result: { reply: ImportReply; rows: ImportRow[]; applied: boolean } | null = null;
  const filters = { preview: "all", result: "all" };
  let fileToken = 0;

  // ---- inputs (built once, so typing and focus are never interrupted) ----------------------------------------------
  const textarea = h("textarea", { id: "import-text", "aria-describedby": "import-parse", spellcheck: "false", autocomplete: "off", placeholder: "slug,business_name,city,state,money_site_url\nacme-tint,Acme Tint,Portland,OR,https://acme.example.test/" });
  const fileInput = h("input", { id: "import-file", type: "file", accept: ".json,.csv,.txt,application/json,text/csv,text/plain" });
  const fileNoteEl = h("p", { class: "qf-imp-why", id: "import-file-note", role: "status" });
  const parseBox = h("div", { id: "import-parse", class: "qf-imp-parse", role: "status", "aria-live": "polite" });

  const radio = (name: string, value: string, label: string, hint: string, checked: boolean, onPick: () => void) => {
    const input = h("input", { type: "radio", name, value, id: `import-${name}-${value}` }) as HTMLInputElement;
    input.checked = checked;
    input.addEventListener("change", () => input.checked && onPick());
    return h("label", { class: "qf-imp-opt", for: `import-${name}-${value}` }, input, h("span", null, label, h("small", null, hint)));
  };
  const optionsBox = h(
    "div",
    { class: "qf-imp-options" },
    h(
      "fieldset",
      { id: "import-existing" },
      h("legend", null, "Clients that already exist (same slug)"),
      radio("existing", "report", "Report them, change nothing", "An existing client whose values differ is listed as \"exists\" and left alone. Identical rows show as unchanged.", true, () => setOption("onExisting", "report")),
      radio("existing", "update", "Update existing clients", "Existing clients are updated with the values in the file, and each change is written to their facts history.", false, () => setOption("onExisting", "update")),
    ),
    h(
      "fieldset",
      { id: "import-error" },
      h("legend", null, "Rows with errors"),
      radio("error", "abort", "Stop if any row is invalid", "Nothing is written unless every row is valid.", true, () => setOption("onError", "abort")),
      radio("error", "skip", "Skip invalid rows", "The valid rows are imported; the invalid ones are listed and skipped.", false, () => setOption("onError", "skip")),
    ),
  );

  const dryBtn = h("button", { type: "button", id: "import-dry-run", "aria-describedby": "import-dry-why", dataset: { focus: "dry" } }, "Run dry run") as HTMLButtonElement;
  const dryWhy = h("p", { id: "import-dry-why", class: "qf-imp-why" });
  const notice = h("div", { id: "import-notice" });
  const previewBox = h("div", { id: "import-preview-slot" });
  const resultBox = h("div", { id: "import-result-slot" });

  const accepted = h(
    "details",
    null,
    h("summary", null, "Accepted columns and names"),
    h("p", null, "Use these column names (CSV header row) or keys (JSON). Empty CSV cells are left out."),
    h("p", { id: "import-columns" }, [...KNOWN_FIELDS].join(", ")),
    h("p", null, "Also understood: ", Object.entries(FIELD_ALIASES).map(([from, to]) => `${from} → ${to}`).join(", "), "."),
    h("p", null, `${POLICY_FIELDS.join(" and ")} are owner-only settings: they are removed if present and are never sent. Services, areas and similar lists may be given as JSON text in a CSV cell, or as real JSON values in a JSON file. At most ${MAX_ROWS} rows per import.`),
  );

  shell.append(
    h("div", { class: "topbar" }, h("h1", { id: "import-title", tabindex: "-1" }, "Import clients"), h("a", { class: "btn secondary", href: "#/" }, "Back to Fleet")),
    h(
      "div",
      { class: "qf-imp-card" },
      h("h2", null, "1. Paste or choose the clients"),
      h("p", { class: "qf-summary" }, "JSON (an array of clients, or {\"clients\": [...]}) or CSV with a header row. The slug is each client's key: importing the same slug again never creates a second client. Nothing is sent until you press Run dry run."),
      h("label", { class: "qf-label", for: "import-text" }, "Clients as JSON or CSV"),
      textarea,
      h("div", { class: "qf-toolbar" }, h("div", { class: "qf-grow" }, h("label", { class: "qf-label", for: "import-file" }, "Or choose a file (.json, .csv, .txt)"), fileInput)),
      fileNoteEl,
      parseBox,
      accepted,
      h("h2", null, "2. Options"),
      optionsBox,
      h("div", { class: "qf-imp-actions" }, dryBtn),
      dryWhy,
    ),
    notice,
    previewBox,
    resultBox,
  );

  // ---- derived state ----------------------------------------------------------------------------------------------
  const locked = () => busy !== null;
  const curKey = () => requestKey(parsed.rows, options);
  const previewIsCurrent = () => preview !== null && preview.key === curKey();
  const dryAllowed = () => !locked() && canDryRun(parsed);

  function setOption<K extends "onExisting" | "onError">(key: K, value: K extends "onExisting" ? OnExisting : OnError) {
    if (locked()) return;
    (options as unknown as Record<string, string>)[key] = value;
    invalidate();
  }

  /** The text, the file or an option changed: any preview, notice or result no longer describes the input. */
  function invalidate() {
    preview = null;
    problem = null;
    result = null;
    paintAll();
  }

  // ---- painting ---------------------------------------------------------------------------------------------------
  function renderParse() {
    const issueItems = parsed.issues.map((i) => h("li", null, i.row !== null ? `Row ${i.row}${i.line !== null ? ` (line ${i.line})` : ""}: ` : i.line !== null ? `Line ${i.line}: ` : "", i.message));
    const kids: (Node | string | null)[] = [];
    if (parsed.format === "empty") {
      parseBox.replaceChildren(h("p", { class: "qf-imp-why" }, "Nothing entered yet."));
      parseBox.dataset.format = "empty";
      parseBox.dataset.rows = "0";
      return;
    }
    parseBox.dataset.format = parsed.format;
    parseBox.dataset.rows = String(parsed.rows.length);
    parseBox.dataset.issues = String(parsed.issues.length);
    kids.push(h("p", null, h("strong", null, `Read ${parsed.rows.length} ${parsed.rows.length === 1 ? "row" : "rows"} as ${parsed.format.toUpperCase()}.`), " Nothing has been sent yet."));
    if (parsed.aliasesUsed.length) kids.push(h("p", { class: "qf-imp-why", dataset: { part: "aliases" } }, "Column names understood as: ", parsed.aliasesUsed.map((a) => `${tidy(a.from, 40)} → ${a.to}`).join(", "), "."));
    if (parsed.unknownFields.length) kids.push(h("p", { class: "qf-imp-why", dataset: { part: "unknown" } }, `Not a known client field (sent as written; the server will say what it thinks): ${parsed.unknownFields.map((f) => tidy(f, 40)).join(", ")}.`));
    if (parsed.strippedPolicy.length) kids.push(h("p", { class: "qf-imp-why", dataset: { part: "policy" } }, `Removed and not sent: ${parsed.strippedPolicy.join(", ")}. These are owner-only settings that an import may not change.`));
    if (parsed.tooMany) kids.push(h("div", { class: "error-banner", role: "alert", dataset: { part: "too-many" } }, `${TOO_MANY_MESSAGE} This input has ${parsed.rows.length} rows: split it into files of at most ${MAX_ROWS} rows and import them one after another.`));
    const otherIssues = parsed.tooMany ? issueItems.filter((_, i) => parsed.issues[i].row !== null || parsed.issues[i].line !== null || !parsed.issues[i].message.includes(TOO_MANY_MESSAGE)) : issueItems;
    if (otherIssues.length) kids.push(h("div", { class: "error-banner", role: "alert", dataset: { part: "issues" } }, h("strong", null, "Fix these before the dry run:"), h("ul", null, ...otherIssues)));
    parseBox.replaceChildren(...(kids.filter(Boolean) as Node[]));
  }

  function syncInputs() {
    textarea.readOnly = locked();
    fileInput.disabled = locked();
    for (const r of optionsBox.querySelectorAll<HTMLInputElement>("input[type=radio]")) {
      r.disabled = locked();
      r.checked = (r.name === "existing" ? options.onExisting : options.onError) === r.value;
    }
    const allowed = dryAllowed();
    dryBtn.setAttribute("aria-disabled", allowed ? "false" : "true");
    if (busy === "dry") dryBtn.setAttribute("aria-busy", "true");
    else dryBtn.removeAttribute("aria-busy");
    dryBtn.textContent = busy === "dry" ? "Running dry run…" : "Run dry run";
    dryWhy.textContent = busy === "dry" ? "Checking every row against the server. Nothing is written." : allowed ? "A dry run writes nothing. It shows what would be created, updated, left alone or rejected, row by row." : parsed.format === "empty" ? "Enter or choose some clients first." : parsed.tooMany ? `Too many rows (${parsed.rows.length}); split the input.` : parsed.rows.length === 0 ? "There are no client rows to check." : "Fix the problems listed above first.";
    fileNoteEl.textContent = fileNote ?? "";
  }

  function rowsBlock(kind: "preview" | "result", reply: ImportReply, rows: ImportRow[], done: boolean): HTMLElement {
    const labels = done ? ACTION_LABELS_DONE : ACTION_LABELS;
    const present = ACTIONS.filter((a) => reply.rows.some((r) => r.action === a));
    const unknown = reply.rows.some((r) => r.action === "unknown");
    const select = h("select", { id: `import-${kind}-filter`, "aria-controls": `import-${kind}-rows`, "aria-label": "Show rows" }) as HTMLSelectElement;
    select.append(h("option", { value: "all" }, `All rows (${reply.rows.length})`));
    for (const a of present) select.append(h("option", { value: a }, `${labels[a]} (${reply.rows.filter((r) => r.action === a).length})`));
    if (unknown) select.append(h("option", { value: "unknown" }, `${labels.unknown} (${reply.rows.filter((r) => r.action === "unknown").length})`));
    if (![...select.options].some((o) => o.value === filters[kind])) filters[kind] = "all";
    select.value = filters[kind];
    const tbody = h("tbody");
    const note = h("p", { class: "qf-summary", role: "status" });
    const paint = () => {
      const visible = reply.rows.filter((r) => filters[kind] === "all" || r.action === filters[kind]);
      note.textContent = `Showing ${visible.length} of ${reply.rows.length} rows. Row numbers count the entries in your input, starting at 1.`;
      tbody.replaceChildren(...visible.map((r) => resultRow(r, rows, labels)));
    };
    select.addEventListener("change", () => {
      filters[kind] = select.value;
      paint();
    });
    paint();
    const table = h(
      "table",
      { class: "qf-table", id: `import-${kind}-rows` },
      h("caption", { class: "qf-sr-only" }, done ? "Import result, row by row" : "Dry run result, row by row"),
      h("thead", null, h("tr", null, ...["Row", "Slug", "Business name", "Result", "Details"].map((t) => h("th", { scope: "col" }, t)))),
      tbody,
    );
    return h("div", null, h("div", { class: "qf-toolbar" }, h("div", null, h("label", { class: "qf-label", for: `import-${kind}-filter` }, "Show"), select)), note, tableRegion(done ? "Import result" : "Dry run result", table));
  }

  function resultRow(r: ResultRow, rows: ImportRow[], labels: Record<RowAction, string>): HTMLElement {
    const sent = rows[r.index];
    const name = sent && typeof sent.business_name === "string" ? tidy(sent.business_name, 120) : "—";
    const details: HTMLElement[] = [
      ...(r.action === "skipped" && (r.plannedAction === "create" || r.plannedAction === "update") ? [h("li", { dataset: { part: "planned" } }, `Would otherwise be ${r.plannedAction === "create" ? "created" : "updated"}; skipped because the whole write is blocked.`)] : []),
      ...r.errors.map((e) => h("li", { dataset: { part: "error" } }, h("strong", null, `${e.field}: `), e.message, e.code ? ` (${e.code})` : "")),
      ...r.diff.map((d) => h("li", { dataset: { part: "diff" } }, `${d.field}: ${d.before} → ${d.after}`)),
    ];
    return h(
      "tr",
      { class: r.action === "error" ? "qf-imp-row--error" : false, dataset: { rowIndex: r.index, action: r.action, slug: r.slug } },
      h("td", null, String(r.index + 1)),
      h("td", { class: "qf-wrap" }, r.slug || "—"),
      h("td", { class: "qf-wrap" }, name),
      h("td", null, pill(labels[r.action], actionTone(r.action))),
      h("td", { class: "qf-imp-details qf-wrap" }, details.length ? h("ul", null, ...details) : h("span", { class: "muted" }, "—")),
    );
  }

  function countsList(reply: ImportReply, done: boolean): HTMLElement {
    const s = reply.summary;
    const L = done ? ACTION_LABELS_DONE : ACTION_LABELS;
    const items: [string, number, string][] = [["total", s.total, "Rows"], ["create", s.create, L.create], ["update", s.update, L.update], ["unchanged", s.unchanged, L.unchanged], ["exists", s.exists, "Exist, not changed"], ["error", s.error, L.error], ["skipped", s.skipped, L.skipped]];
    const ul = h("ul", { class: "qf-imp-counts", id: done ? "import-result-summary" : "import-summary" });
    for (const [k, n, label] of items) {
      ul.dataset[k] = String(n);
      ul.append(h("li", { dataset: { count: k } }, h("b", null, String(n)), label));
    }
    return ul;
  }

  function renderNotice() {
    if (!problem) return void notice.replaceChildren();
    const box = h(
      "div",
      { class: problem.kind === "uncertain" ? "qf-notice qf-notice--warning" : "qf-notice qf-notice--error", role: "alert", id: "import-problem", tabindex: "-1", dataset: { kind: problem.kind } },
      h("div", null, h("strong", null, problem.headline), problem.detail ? h("p", { class: "qf-imp-why" }, problem.detail) : null),
    );
    notice.replaceChildren(box);
  }

  function renderPreview() {
    if (!preview || !previewIsCurrent()) return void previewBox.replaceChildren();
    const { reply, rows } = preview;
    const plan = planFor(reply.summary, options, reply.confirm);
    const confirmBtn = h("button", { type: "button", id: "import-confirm", "aria-describedby": "import-confirm-why", dataset: { focus: "confirm" } }, busy === "confirm" ? "Importing…" : "Confirm import") as HTMLButtonElement;
    const blocked = busy !== null || !plan.canConfirm;
    confirmBtn.setAttribute("aria-disabled", blocked ? "true" : "false");
    if (busy === "confirm") confirmBtn.setAttribute("aria-busy", "true");
    confirmBtn.addEventListener("click", (ev) => {
      if (blocked) return void ev.preventDefault();
      void runConfirm();
    });
    previewBox.replaceChildren(
      h(
        "div",
        { class: "qf-imp-card", id: "import-preview", dataset: { dryRun: "true", applied: "false" } },
        h("h2", { id: "import-preview-title", tabindex: "-1" }, "3. Dry run result: nothing has been written"),
        countsList(reply, false),
        rowsBlock("preview", reply, rows, false),
      ),
      h(
        "div",
        { class: "qf-imp-card", id: "import-confirm-area" },
        h("h2", null, "4. Confirm"),
        h("p", { class: "qf-imp-plan", id: "import-plan", dataset: { canConfirm: plan.canConfirm ? "true" : "false" } }, h("strong", null, plan.willDo)),
        plan.willNot ? h("p", { class: "qf-imp-plan", id: "import-plan-not" }, `Will not: ${plan.willNot}`) : null,
        ...plan.caveats.map((c) => h("p", { class: "qf-imp-why", dataset: { caveat: "true" } }, c)),
        h("div", { class: "qf-imp-actions" }, confirmBtn),
        h("p", { class: "qf-imp-why", id: "import-confirm-why" }, busy === "confirm" ? "Writing the clients. Please wait; do not press it again." : plan.canConfirm ? "This writes the clients above. It is exactly what the dry run showed, for exactly this input and these options." : plan.blockedReason),
      ),
    );
  }

  function renderResult() {
    if (!result) return void resultBox.replaceChildren();
    const { reply, rows, applied } = result;
    const linkable = reply.rows.filter((r) => (r.action === "create" || r.action === "update") && r.clientId !== null);
    const links =
      applied && linkable.length > 0 && linkable.length <= FEW
        ? h("ul", { id: "import-result-links" }, ...linkable.map((r) => h("li", null, h("a", { href: clientHref(r.clientId) }, tidy(typeof rows[r.index]?.business_name === "string" ? (rows[r.index].business_name as string) : r.slug, 120)))))
        : null;
    resultBox.replaceChildren(
      h(
        "div",
        { class: "qf-imp-card", id: "import-result", dataset: { applied: applied ? "true" : "false" } },
        h("h2", { id: "import-result-title", tabindex: "-1" }, applied ? "Import finished" : "Nothing was written: the server's per-row report"),
        applied ? h("p", { class: "qf-summary" }, "The clients below were written. Importing the same input again is safe: rows already stored show as unchanged.") : h("p", { class: "qf-summary" }, "The server applied nothing. Rows marked Skipped were valid but were not imported because another row is invalid."),
        countsList(reply, applied),
        h("div", { class: "qf-imp-actions" }, h("a", { class: "btn", id: "import-to-fleet", href: "#/" }, "Go to Fleet")),
        links,
        rowsBlock("result", reply, rows, applied),
      ),
    );
  }

  function paintAll() {
    renderParse();
    syncInputs();
    renderNotice();
    renderPreview();
    renderResult();
  }

  // ---- actions ----------------------------------------------------------------------------------------------------
  /** A 2xx body, or the ApiError of a refusal. Anything else (a dropped connection) is thrown to the caller. */
  async function post(rows: ImportRow[], dryRun: boolean, signal: AbortSignal): Promise<{ body: unknown; error: ApiError | null }> {
    try {
      return { body: await api<unknown>("/v1/clients/import", { method: "POST", body: JSON.stringify(buildBody(rows, options, dryRun)), signal }), error: null };
    } catch (e) {
      if (e instanceof ApiError) return { body: null, error: e };
      throw e;
    }
  }
  const errOf = (r: { error: ApiError | null }): ApiError | null => r.error;

  async function runDry() {
    if (!dryAllowed()) return;
    const rows = parsed.rows;
    const key = curKey();
    busy = "dry";
    problem = null;
    result = null;
    preview = null;
    paintAll();
    const req = life.begin();
    let outcome: { preview?: Preview; problem?: Problem } = {};
    try {
      const r = await post(rows, true, req.signal);
      if (!req.current()) return;
      const e = errOf(r);
      if (e) {
        const d = describeFailure(e.status, e.code ?? null, e.message, "dry");
        outcome = { problem: { kind: "refused", headline: d.headline, detail: d.detail } };
      } else {
        const reply = parseImportReply(r.body);
        outcome = isValidPreview(reply, rows.length) ? { preview: { key, rows, reply } } : { problem: { kind: "refused", headline: "The server answered, but not with a dry run result this page can trust, so nothing is shown as a preview.", detail: "A dry run writes nothing, so it is safe to run it again." } };
      }
    } catch (e) {
      if (isAbort(e) || !req.current()) return;
      const d = describeFailure(null, null, null, "dry");
      outcome = { problem: { kind: "refused", headline: d.headline, detail: d.detail } };
    }
    busy = null;
    preview = outcome.preview ?? null;
    problem = outcome.problem ?? null;
    paintAll();
    (shell.querySelector<HTMLElement>(preview ? "#import-preview-title" : "#import-problem") ?? dryBtn).focus();
  }

  async function runConfirm() {
    if (busy !== null || !preview || !previewIsCurrent()) return;
    const plan = planFor(preview.reply.summary, options, preview.reply.confirm);
    if (!plan.canConfirm) return;
    const { rows } = preview;
    busy = "confirm";
    problem = null;
    paintAll();
    const req = life.begin();
    let next: { problem?: Problem; result?: { reply: ImportReply; rows: ImportRow[]; applied: boolean } } = {};
    try {
      const r = await post(rows, false, req.signal);
      if (!req.current()) return;
      const e = errOf(r);
      if (e) {
        const d = describeFailure(e.status, e.code ?? null, e.message, "confirm");
        const reply = e.status === 422 ? parseImportReply(e.body) : null;
        next = { problem: { kind: d.kind, headline: d.headline, detail: d.detail }, result: reply && !reply.applied && !reply.dryRun ? { reply, rows, applied: false } : undefined };
      } else {
        const reply = parseImportReply(r.body);
        next = isAppliedReply(reply, rows.length)
          ? { result: { reply, rows, applied: true } }
          : { problem: { kind: "uncertain", headline: "The server answered, but not with a confirmation this page can read, so it is not known whether any rows were written.", detail: "Do not send it again blindly. Run the dry run again: rows that were already created now show as unchanged." } };
      }
    } catch (e) {
      if (isAbort(e) || !req.current()) return;
      const d = describeFailure(null, null, null, "confirm");
      next = { problem: { kind: d.kind, headline: d.headline, detail: d.detail } };
    }
    // A confirm attempt always ends the preview: another write needs another dry run of what is now stored.
    busy = null;
    preview = null;
    problem = next.problem ?? null;
    result = next.result ?? null;
    paintAll();
    (shell.querySelector<HTMLElement>(result ? "#import-result-title" : "#import-problem") ?? dryBtn).focus();
  }

  async function onFile() {
    const file = fileInput.files?.[0];
    if (!file || locked()) return;
    const mine = ++fileToken;
    fileInput.value = "";
    if (!FILE_RE.test(file.name)) {
      fileNote = `"${tidy(file.name, 80)}" is not a .json, .csv or .txt file, so it was not read.`;
      return void syncInputs();
    }
    if (file.size > MAX_FILE_BYTES) {
      fileNote = `"${tidy(file.name, 80)}" is larger than 2 MB, so it was not read. An import takes at most ${MAX_ROWS} rows: split the file.`;
      return void syncInputs();
    }
    let content: string;
    try {
      content = await file.text();
    } catch {
      fileNote = `"${tidy(file.name, 80)}" could not be read.`;
      return void syncInputs();
    }
    if (mine !== fileToken || !life.alive() || locked()) return;
    textarea.value = content;
    parsed = parseInput(content);
    fileNote = `Loaded ${tidy(file.name, 80)} (${file.size} bytes). It is shown above; nothing has been sent.`;
    invalidate();
  }

  textarea.addEventListener("input", () => {
    if (locked()) return;
    fileToken++; // a file still being read must not overwrite what was just typed
    fileNote = null;
    parsed = parseInput(textarea.value);
    invalidate();
  });
  fileInput.addEventListener("change", () => void onFile());
  dryBtn.addEventListener("click", (ev) => {
    if (!dryAllowed()) return void ev.preventDefault();
    void runDry();
  });

  paintAll();
}
